// src/components/ApplicationSuccess.js
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import logo from "../assets/images/luth-logo-192.png";
import logo1 from "../assets/images/luth-logo-16x16.png";
import logoprint from "../assets/images/luth-logo-small.png";
import "../assets/css/applicationsuccess.css";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

function getProgrammeValue(biodata) {
  const raw =
    biodata?.programme ??
    biodata?.Programme ??
    localStorage.getItem("programme") ??
    "";

  if (Array.isArray(raw)) {
    const found = raw.find(
      (p) => p && String(p).trim().toLowerCase() !== "null",
    );
    return String(found || "").trim();
  }

  const value = String(raw || "").trim();
  return value.toLowerCase() === "null" ? "" : value;
}

export default function ApplicationSuccess({ biodata }) {
  const printRef = useRef(null);
  const navigate = useNavigate();
  const [latestStatus, setLatestStatus] = useState("Admission Processing");

  useEffect(() => {
    const fetchLatestStatus = async () => {
      try {
        const email = biodata?.Email;
        const appno = biodata?.ApplicationNo || biodata?.applicationNo;

        if (!email && !appno) return;

        const res = await axios.get(`${API_BASE_URL}/api/admission-status`, {
          params: { email, appno },
        });

        setLatestStatus(
          res.data?.data?.AdmissionStatus || "Admission Processing",
        );
      } catch (err) {
        console.error("Failed to fetch latest admission status:", err);
      }
    };

    fetchLatestStatus();
  }, [biodata]);

  if (!biodata) return <p>No biodata found.</p>;

  const programme = getProgrammeValue(biodata);
  const institutions = biodata?.institutions || [];
  const exams = biodata?.exams || [];
  const documents = biodata?.documents || [];
  const admissionStatus = biodata?.AdmissionStatus || "Admission Processing";

  const jambEligibleProgrammes = [
    "Department of Nursing Sciences",
    "Department of Nursing Sciences",
    "School of Health Information Management (ND)",
    "Biomedical Engineering (ND)",
  ];

  const showJamb = jambEligibleProgrammes.includes(programme);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-GB");
  };

  const handlePrint = () => {
    const printContents = printRef.current?.innerHTML || "";
    const win = window.open("", "", "height=700,width=900");
    if (!win) return;

    win.document.write("<html><head><title>Print Biodata</title>");
    win.document.write(
      `<style>
        body {
          font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif;
          margin: 0;
          font-size: 12px;
        }
        .watermark {
          display: block;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0.1;
          width: 300px;
          z-index: -1;
        }
        .logo-print {
          width: 80px;
          height: auto;
          object-fit: cover;
          border: 1px solid #ccc;
        }
        .section-title {
          font-weight: bold;
          margin-top: 20px;
          font-size: 1.2rem;
          border-bottom: 2px solid #ccc;
          padding-bottom: 5px;
        }
        .print-area {
          position: relative;
        }
        .print-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .print-title {
          color: green;
          text-align: center;
          font-size: 1.1rem;
          margin-bottom: 0.2rem;
        }
        .print-subtitle {
          color: #333;
          text-align: center;
          font-size: 1rem;
          margin-bottom: 0.2rem;
        }
        .print-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 0.6rem;
          margin-bottom: 1rem;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }
        td, th {
          padding: 6px;
          vertical-align: top;
          border: 1px solid #ddd;
        }
        .passport-print {
          float: right;
          width: 120px;
          height: 140px;
          object-fit: cover;
          border: 1px solid #ccc;
        }
        p.note {
          margin-top: 1.5rem;
          font-style: italic;
        }
      </style>`,
    );
    win.document.write("</head><body>");
    win.document.write(printContents);
    win.document.write("</body></html>");
    win.document.close();

    const triggerPrint = () => {
      win.focus();
      win.print();
      win.close();
    };

    if (win.document.readyState === "complete") {
      triggerPrint();
    } else {
      win.onload = triggerPrint;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("programme");
    localStorage.removeItem("appno");
    localStorage.removeItem("biodata");
    window.location.href = "/applicant-login";
  };

  return (
    <div className="application-success-page">
      <div className="header">
        <div className="header-top">
          <img src={logo} alt="School Logo" className="logo" />
          <div className="header-text">
            <h2>Biodata Successfully Submitted</h2>
            <p className="greeting">
              Dear <strong>{biodata.FirstName}</strong>, you have successfully
              submitted your biodata information for the{" "}
              <strong>{programme || "your selected programme"}</strong>.
              <br />
              Your application number is{" "}
              <strong>{biodata.applicationNo || biodata.ApplicationNo}</strong>.
            </p>
          </div>

          {biodata.passportUrl && (
            <img
              src={biodata.passportUrl}
              alt="Passport"
              className="header-passport"
            />
          )}
        </div>

        <div className="status-summary">
          <div className="status-card">
            <span className="status-label">Admission Status</span>
            <span className="status-value">{latestStatus}</span>
          </div>

          <div className="status-card">
            <span className="status-label">Programme</span>
            <span className="status-value">{programme || "-"}</span>
          </div>

          <div className="status-card">
            <span className="status-label">Application No.</span>
            <span className="status-value">
              {biodata.applicationNo || biodata.ApplicationNo || "-"}
            </span>
          </div>
        </div>

        <div className="actions">
          <button onClick={handlePrint}>Print Biodata</button>
          <button onClick={() => navigate("/applicant-login")}>
            Back to Login
          </button>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div ref={printRef} className="print-area">
        <img src={logo1} alt="Watermark" className="watermark" />

        <div className="print-header">
          <img src={logoprint} alt="Logo Print" className="logo-print" />
          <div className="print-header-text">
            <div className="print-title">
              <h2>
                LAGOS UNIVERSITY TEACHING HOSPITAL, (LUTH) IDI-ARABA, LAGOS
              </h2>
            </div>
            <div className="print-subtitle">
              <h3>APPLICANT BIODATA FORM</h3>
            </div>
          </div>

          {biodata.passportUrl && (
            <img
              src={biodata.passportUrl}
              alt="Passport Print"
              className="passport-print"
            />
          )}
        </div>

        <div className="prog-appno">
          <div
            style={{
              float: "right",
              fontWeight: "bold",
              fontSize: "2rem",
              color: "silver",
              textShadow: "1px 1px 2px rgba(0, 0, 0, 0.3)",
              border: "2px solid silver",
              padding: "0.5rem 1rem",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #f0f0f0, #d9d9d9)",
              display: "inline-block",
            }}
          >
            {new Date().getFullYear()}
          </div>

          <p>
            <strong>School:</strong> {programme || "-"}
          </p>
          <p>
            <strong>Application No:</strong>{" "}
            {biodata.applicationNo || biodata.ApplicationNo || "-"}
          </p>
          <p>
            <strong>Admission Status:</strong> {admissionStatus}
          </p>
        </div>

        <div className="print-row">
          <div>
            <div className="section-title">Personal Information</div>
            <table>
              <tbody>
                <tr>
                  <td>Surname</td>
                  <td>{biodata.LastName}</td>
                </tr>
                <tr>
                  <td>First Name</td>
                  <td>{biodata.FirstName}</td>
                </tr>
                <tr>
                  <td>Middle Name</td>
                  <td>{biodata.MiddleName}</td>
                </tr>
                <tr>
                  <td>Gender</td>
                  <td>{biodata.Gender}</td>
                </tr>
                <tr>
                  <td>DOB</td>
                  <td>{formatDate(biodata.DateOfBirth)}</td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td>{biodata.PhoneNumber || biodata.Phone}</td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td>{biodata.Email}</td>
                </tr>
                <tr>
                  <td>Admission Status</td>
                  <td>{admissionStatus}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            {showJamb && (
              <>
                <div className="section-title">JAMB Details</div>
                <table>
                  <tbody>
                    <tr>
                      <td>Reg No</td>
                      <td>{biodata.JambRegNumber}</td>
                    </tr>
                    <tr>
                      <td>Year</td>
                      <td>{biodata.JambYear}</td>
                    </tr>
                    <tr>
                      <td>Exam No</td>
                      <td>{biodata.JambExamNumber}</td>
                    </tr>
                    <tr>
                      <td>Score</td>
                      <td>{biodata.JambScore}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>

        <div className="print-row">
          <div>
            <div className="section-title">Next of Kin</div>
            <table>
              <tbody>
                <tr>
                  <td>Name</td>
                  <td>{biodata.NextOfKin}</td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td>{biodata.NextOfKinPhone}</td>
                </tr>
                <tr>
                  <td>Address</td>
                  <td>{biodata.NextOfKinAddress}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <div className="section-title">Contact & Background</div>
            <table>
              <tbody>
                <tr>
                  <td>State of Origin</td>
                  <td>{biodata.StateOfOrigin || biodata.State}</td>
                </tr>
                <tr>
                  <td>Local Government</td>
                  <td>{biodata.LocalGovernment || biodata.LocalGov}</td>
                </tr>
                <tr>
                  <td>Nationality</td>
                  <td>{biodata.Nationality}</td>
                </tr>
                <tr>
                  <td>Religion</td>
                  <td>{biodata.Religion}</td>
                </tr>
                <tr>
                  <td>Denomination</td>
                  <td>{biodata.Denomination}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="print-row">
          <div>
            <div className="section-title">Institutions Attended</div>
            {institutions.length ? (
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Qualification</th>
                    <th>Years</th>
                  </tr>
                </thead>
                <tbody>
                  {institutions.map((inst, i) => (
                    <tr key={i}>
                      <td>{inst.InstitutionName}</td>
                      <td>{inst.QualificationObtained}</td>
                      <td>
                        {inst.StartYear} - {inst.EndYear}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No institutions recorded.</p>
            )}
          </div>

          <div>
            <div className="section-title">Exams Passed</div>
            {exams.length ? (
              exams.map((exam, i) => (
                <div key={i}>
                  <p>
                    <strong>
                      {exam.ExamName} (Sitting {exam.Sittings})
                    </strong>
                  </p>
                  <table>
                    <tbody>
                      {Object.entries(exam.SubjectsAndGrades || {}).map(
                        ([sub, gr]) => (
                          <tr key={sub}>
                            <td>{sub}</td>
                            <td>{gr}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              ))
            ) : (
              <p>No exams passed.</p>
            )}
          </div>
        </div>

        <div className="print-row">
          <div>
            <div className="section-title">Supporting Documents</div>
            {documents.length ? (
              <ul>
                {documents.map((doc, idx) => (
                  <li key={idx}>
                    {typeof doc === "string"
                      ? `Document ${idx + 1}`
                      : doc?.name || `Document ${idx + 1}`}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No supporting documents attached.</p>
            )}
          </div>
        </div>

        <p className="note">
          <strong>Note:</strong>
          <br />
          1. Kindly come with the biodata form to the exam hall.
          <br />
          2. Bags and electronic devices are not allowed in the exam hall.
          <br />
          3. All exams hold the same day.
          <br />
          4. If you miss the exam, you will not be rescheduled.
        </p>
      </div>
    </div>
  );
}